import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import './MsmeImpactTable.css';

const MsmeImpactTable = ({ selectedGender, selectedAgeRange, selectedProjectName, selectedDateRange }) => {
  const [stateData, setStateData] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch('/db2_msmeimpact.json'); // ✅ same JSON as map
        if (!response.ok) throw new Error('Failed to load MSME Impact data');
        const data = await response.json();

        const aggregatedData = data.reduce((acc, curr) => {
          if (!curr['st_nm'] || !curr['Gender']) return acc;

          const genderMatch = !selectedGender || curr['Gender'].toLowerCase() === selectedGender.toLowerCase();
          const ageRangeMatch = !selectedAgeRange || curr['Age_Range'] === selectedAgeRange;
          const projectNameMatch = !selectedProjectName || curr['Project Name'] === selectedProjectName;

          // ✅ Date range filter
          let dateRangeMatch = true;
          if (selectedDateRange?.start && selectedDateRange?.end) {
            const currDate = new Date(curr["MonthYear"]);
            const startDate = new Date(selectedDateRange.start);
            const endDate = new Date(selectedDateRange.end);
            dateRangeMatch = currDate >= startDate && currDate <= endDate;
          }

          if (genderMatch && ageRangeMatch && projectNameMatch && dateRangeMatch) {
            const stateName = curr['st_nm'] ? curr['st_nm'].trim().toLowerCase() : 'unknown';
            const msmeCount = parseInt(curr['Total_MSMEs'] || curr['Total_Citizens'] || 0, 10); // ✅ safer field handling
            const gender = curr['Gender'].toLowerCase();

            if (!acc[stateName]) {
              acc[stateName] = {
                state: curr['st_nm'].trim() || 'unknown',
                maleCount: 0,
                femaleCount: 0,
                othersCount: 0,
                totalMsmesCount: 0,
              };
            }

            if (gender === 'male') acc[stateName].maleCount += msmeCount;
            else if (gender === 'female') acc[stateName].femaleCount += msmeCount;
            else acc[stateName].othersCount += msmeCount;

            acc[stateName].totalMsmesCount += msmeCount;
          }

          return acc;
        }, {});

        setStateData(Object.values(aggregatedData));
      } catch (error) {
        console.error('Error loading state data:', error);
      }
    };

    fetchData();
  }, [selectedGender, selectedAgeRange, selectedProjectName, selectedDateRange]);

  // ✅ Export to Excel (matches table view)
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      stateData.map(d => ({
        State: d.state,
        'Male MSMEs': d.maleCount,
        'Female MSMEs': d.femaleCount,
        'Other MSMEs': d.othersCount,
        'Total MSMEs': d.totalMsmesCount,
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'MSME Impact Data');
    XLSX.writeFile(workbook, 'msme_impact_data.xlsx');
  };

  return (
    <div className="msme-table-container">
      <button onClick={exportToExcel} className="msme-export-btn">Export to Excel</button>
      <table>
        <thead>
          <tr>
            <th>State</th>
            <th>Male MSMEs</th>
            <th>Female MSMEs</th>
            <th>Other MSMEs</th>
            <th>Total MSMEs</th>
          </tr>
        </thead>
        <tbody>
          {stateData.map((data, index) => (
            <tr key={index}>
              <td>{data.state || 'Unknown'}</td>
              <td>{data.maleCount.toLocaleString()}</td>
              <td>{data.femaleCount.toLocaleString()}</td>
              <td>{data.othersCount.toLocaleString()}</td>
              <td>{data.totalMsmesCount.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default MsmeImpactTable;
